import DashboardLayout from "@/components/DashboardLayout";
import { AnimatedCounter, AnimatedCurrency, AnimatedPercentage } from "@/components/AnimatedCounter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { trpc } from "@/lib/trpc";
import { useRoute } from "wouter";
import {
  DollarSign,
  Users,
  TrendingUp,
  Phone,
  Mail,
  BarChart3,
  ArrowLeft,
  Building2,
  Calendar,
  Target,
  Wallet,
  Activity,
  PieChart,
} from "lucide-react";
import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart as RechartsPie,
  Pie,
} from "recharts";
import { Link } from "wouter";

const HEIGHTS_PLACEMENT_ID = 30002;

const CHART_COLORS = [
  "#f59e0b", // amber
  "#10b981", // emerald
  "#3b82f6", // blue
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#06b6d4", // cyan
  "#f97316", // orange
  "#6366f1", // indigo
];

function toNumber(val: any): number {
  if (val === null || val === undefined) return 0;
  const n = typeof val === "string" ? parseFloat(val) : val;
  return isNaN(n) ? 0 : n;
}

function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return "$0";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "$0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(num);
}

function formatNumber(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return "0";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("en-US").format(num);
}

export default function PlacementDetail() {
  // Get placement ID from route params, default to Heights Finance
  const [, params] = useRoute("/placement/:id");
  const placementId = params?.id ? parseInt(params.id) : HEIGHTS_PLACEMENT_ID;

  const { data: detail, isLoading: detailLoading } = trpc.placementDetail.summary.useQuery({ placementId });
  const { data: balanceDist, isLoading: distLoading } = trpc.placementDetail.balanceDistribution.useQuery({ placementId });
  const { data: activity, isLoading: activityLoading } = trpc.placementDetail.collectionActivity.useQuery({ placementId });

  const isLoading = detailLoading || distLoading || activityLoading;

  // Process balance distribution for chart
  const balanceChartData = useMemo(() => {
    if (!balanceDist || !Array.isArray(balanceDist)) return [];
    return balanceDist.map((b: any) => ({
      range: b.balance_range,
      accounts: parseInt(b.account_count),
      totalBalance: parseFloat(b.total_balance),
      avgBalance: parseFloat(b.avg_balance),
    }));
  }, [balanceDist]);

  // Process status data for donut chart
  const statusChartData = useMemo(() => {
    if (!detail) return [];
    const data = [];
    if (toNumber(detail.active_accounts) > 0) data.push({ name: "Active", value: toNumber(detail.active_accounts), color: "#3b82f6" });
    if (toNumber(detail.settled_accounts) > 0) data.push({ name: "Settled", value: toNumber(detail.settled_accounts), color: "#10b981" });
    if (toNumber(detail.payment_plan_accounts) > 0) data.push({ name: "Payment Plan", value: toNumber(detail.payment_plan_accounts), color: "#f59e0b" });
    if (toNumber(detail.recalled_accounts) > 0) data.push({ name: "Recalled", value: toNumber(detail.recalled_accounts), color: "#ef4444" });
    return data;
  }, [detail]);

  // Contact coverage percentages
  const phoneCoverage = detail ? (toNumber(detail.accounts_with_phone) / toNumber(detail.loaded_accounts) * 100) : 0;
  const emailCoverage = detail ? (toNumber(detail.accounts_with_email) / toNumber(detail.loaded_accounts) * 100) : 0;

  // Custom tooltip for balance chart
  const BalanceTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div className="bg-popover text-popover-foreground border border-border rounded-lg p-3 shadow-lg">
        <p className="font-semibold text-sm">{label}</p>
        <p className="text-sm text-muted-foreground mt-1">
          <span className="text-amber-400 font-medium">{formatNumber(payload[0]?.value)}</span> accounts
        </p>
        <p className="text-sm text-muted-foreground">
          Total: <span className="text-emerald-400 font-medium">{formatCurrency(payload[0]?.payload?.totalBalance)}</span>
        </p>
        <p className="text-sm text-muted-foreground">
          Avg: <span className="text-blue-400 font-medium">{formatCurrency(payload[0]?.payload?.avgBalance)}</span>
        </p>
      </div>
    );
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Link href="/placements">
                <button className="p-1.5 rounded-md hover:bg-accent transition-colors">
                  <ArrowLeft className="h-5 w-5 text-muted-foreground" />
                </button>
              </Link>
              <h1 className="text-2xl font-bold tracking-tight">
                {detail?.placement_name || "Placement Detail"}
              </h1>
              <Badge variant="outline" className="text-emerald-400 border-emerald-400/30">
                {detail?.status || "active"}
              </Badge>
            </div>
            <p className="text-muted-foreground ml-10">
              {detail?.portfolio_type} — Placed{" "}
              {detail?.placement_date
                ? new Date(detail.placement_date).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })
                : "N/A"}
            </p>
          </div>
        </div>

        {/* Top-Level KPIs */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="border-l-4 border-l-amber-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Face Value</CardTitle>
              <DollarSign className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                <AnimatedCurrency value={toNumber(detail?.total_face_value)} duration={2000} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Balance at placement
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Accounts</CardTitle>
              <Users className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                <AnimatedCounter value={toNumber(detail?.loaded_accounts)} duration={2000} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Loaded into system
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Collected</CardTitle>
              <TrendingUp className="h-4 w-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                <AnimatedCurrency value={toNumber(detail?.total_collected)} duration={2000} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Gross collections
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-violet-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Liquidation Rate</CardTitle>
              <Target className="h-4 w-4 text-violet-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                <AnimatedPercentage value={toNumber(detail?.liquidation_rate)} duration={2000} decimals={2} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Collected / Face Value
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Balance Metrics + Account Status */}
        <div className="grid gap-4 md:grid-cols-3">
          {/* Average Balance Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Wallet className="h-4 w-4 text-muted-foreground" />
                Balance Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Average Balance</span>
                  <span className="font-semibold">{formatCurrency(detail?.avg_account_balance)}</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Min Balance</span>
                  <span className="font-semibold">{formatCurrency(detail?.min_account_balance)}</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Max Balance</span>
                  <span className="font-semibold">{formatCurrency(detail?.max_account_balance)}</span>
                </div>
              </div>
              <div className="border-t border-border pt-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Current Outstanding</span>
                  <span className="font-semibold text-amber-400">{formatCurrency(detail?.total_current_balance)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount Recovered</span>
                  <span className="font-semibold text-emerald-400">{formatCurrency(detail?.total_collected)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Status Donut */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <PieChart className="h-4 w-4 text-muted-foreground" />
                Account Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              {statusChartData.length > 0 ? (
                <div className="flex items-center gap-4">
                  <div className="w-32 h-32">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPie>
                        <Pie
                          data={statusChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={30}
                          outerRadius={55}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {statusChartData.map((entry: any, index: number) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                      </RechartsPie>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-2">
                    {statusChartData.map((entry: any) => (
                      <div key={entry.name} className="flex items-center gap-2 text-sm">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                        <span className="text-muted-foreground">{entry.name}</span>
                        <span className="font-semibold ml-auto">{formatNumber(entry.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No data available</p>
              )}
            </CardContent>
          </Card>

          {/* Contact Coverage */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                Contact Coverage
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <div className="flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5 text-blue-400" />
                    <span className="text-muted-foreground">Phone Numbers</span>
                  </div>
                  <span className="font-semibold">
                    {formatNumber(detail?.accounts_with_phone)} / {formatNumber(detail?.loaded_accounts)}
                  </span>
                </div>
                <Progress value={phoneCoverage} className="h-2" />
                <p className="text-xs text-muted-foreground mt-1">{phoneCoverage.toFixed(1)}% coverage</p>
              </div>
              <div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <div className="flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 text-emerald-400" />
                    <span className="text-muted-foreground">Email Addresses</span>
                  </div>
                  <span className="font-semibold">
                    {formatNumber(detail?.accounts_with_email)} / {formatNumber(detail?.loaded_accounts)}
                  </span>
                </div>
                <Progress value={emailCoverage} className="h-2" />
                <p className="text-xs text-muted-foreground mt-1">{emailCoverage.toFixed(1)}% coverage</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Balance Distribution Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Balance Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {balanceChartData.length > 0 ? (
              <div style={{ height: "320px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={balanceChartData} margin={{ top: 10, right: 30, left: 10, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                    <XAxis
                      dataKey="range"
                      tick={{ fill: "#ffffff", fontSize: 11 }}
                      axisLine={{ stroke: "hsl(var(--border))" }}
                      angle={-20}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis
                      tick={{ fill: "#ffffff", fontSize: 12 }}
                      axisLine={{ stroke: "hsl(var(--border))" }}
                      tickFormatter={(value) => formatNumber(value)}
                    />
                    <Tooltip content={<BalanceTooltip />} />
                    <Bar dataKey="accounts" radius={[4, 4, 0, 0]} maxBarSize={60}>
                      {balanceChartData.map((_: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No balance data available</p>
            )}
          </CardContent>
        </Card>

        {/* Collection Activity */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                Collection Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Total Calls Made</p>
                  <p className="text-3xl font-bold">
                    <AnimatedCounter value={activity?.calls || 0} duration={1500} />
                  </p>
                  {activity?.calls === 0 && (
                    <p className="text-xs text-amber-400">Calling begins soon</p>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Payments Received</p>
                  <p className="text-3xl font-bold">
                    <AnimatedCounter value={activity?.payments || 0} duration={1500} />
                  </p>
                  {activity?.payments === 0 && (
                    <p className="text-xs text-amber-400">Awaiting first payment</p>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Payment Volume</p>
                  <p className="text-3xl font-bold text-emerald-400">
                    <AnimatedCurrency value={activity?.paymentAmount || 0} duration={1500} />
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Remaining Balance</p>
                  <p className="text-3xl font-bold text-amber-400">
                    <AnimatedCurrency value={toNumber(detail?.total_current_balance)} duration={1500} />
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Placement Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Placement Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Portfolio Type</span>
                  <span className="text-sm font-medium">{detail?.portfolio_type}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Placement Date</span>
                  <span className="text-sm font-medium">
                    {detail?.placement_date
                      ? new Date(detail.placement_date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "N/A"}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Total Accounts</span>
                  <span className="text-sm font-medium">{formatNumber(detail?.total_accounts)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Face Value</span>
                  <span className="text-sm font-medium">{formatCurrency(detail?.total_face_value)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Collection Fee</span>
                  <span className="text-sm font-medium">{toNumber(detail?.collection_fee_percent).toFixed(2)}%</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <Badge variant="outline" className="text-emerald-400 border-emerald-400/30">
                    {detail?.status}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Payments Table */}
        {activity?.recentPayments && activity.recentPayments.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Recent Payments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 text-muted-foreground font-medium">Debtor</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Account</th>
                      <th className="text-right py-2 text-muted-foreground font-medium">Amount</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Date</th>
                      <th className="text-left py-2 text-muted-foreground font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activity.recentPayments.map((p: any, i: number) => (
                      <tr key={i} className="border-b border-border/50">
                        <td className="py-2">{p.debtor_name}</td>
                        <td className="py-2 text-muted-foreground">{p.account_number}</td>
                        <td className="py-2 text-right font-medium text-emerald-400">{formatCurrency(p.amount)}</td>
                        <td className="py-2 text-muted-foreground">
                          {p.payment_date ? new Date(p.payment_date).toLocaleDateString() : "N/A"}
                        </td>
                        <td className="py-2">
                          <Badge variant="outline" className="text-xs">
                            {p.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
